import axios from 'axios'
import { config } from '../config/environment'
import { getAuthToken, setAuthToken } from './authToken'

const API_BASE_URL = config.apiBaseUrl

export interface LoginRequest {
  email: string
  password: string
}

export interface SignupRequest {
  email: string
  password: string
}

export interface LoginResponse {
  token: string
  access_token?: string  // For backward compatibility
  token_type?: string
  user: {
    id: string
    email: string
    emailVerified: boolean
    displayName: string
  }
  userData: {
    role: string
    companyId: string | null
    name: string | null
    permissions: string[]
  }
}

export interface User {
  id: number
  email: string
  role: string
  onboardingState?: string
}

/** A session check that did not come back with a clear answer. `status` is
 * absent for a network failure (the request never reached the API). */
export class AuthCheckError extends Error {
  status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'AuthCheckError'
    this.status = status
  }

  /** True when this says nothing about whether the session cookie is still
   * good — a deploy mid-rollover, a cold start, a dropped connection — so
   * retrying beats treating it as a logout. A 401/403 is the one status that
   * is a real answer: there is no session (or it genuinely expired). */
  get isRecoverable(): boolean {
    return this.status === undefined || this.status >= 500
  }
}

// Create axios instance with default config
const authApi = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor to include the in-memory token (backward compat).
authApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle auth errors
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || ''
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/signup')
    const onAuthPage =
      window.location.pathname === '/login' || window.location.pathname === '/signup'
    // Hard-redirect only when an in-memory token existed — i.e. a session this
    // tab was actually using expired. An anonymous visitor's session-restore
    // probe (/auth/me with no token) also 401s; redirecting on that reloads
    // /login in an infinite loop. Route gating for anonymous users is
    // ProtectedRoute's job, not this interceptor's.
    if (error.response?.status === 401 && !isAuthEndpoint && !onAuthPage && getAuthToken()) {
      setAuthToken(null)
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const authService = {
  // Signup user
  async signup(credentials: SignupRequest): Promise<LoginResponse> {
    try {
      const response = await authApi.post<LoginResponse>('/auth/signup', credentials)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.detail || 'Signup failed')
      }
      throw new Error('An unexpected error occurred')
    }
  },

  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await authApi.post<LoginResponse>('/auth/login', credentials)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.detail || 'Login failed')
      }
      throw new Error('An unexpected error occurred')
    }
  },

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    try {
      const response = await authApi.get<User>('/auth/me')
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new AuthCheckError(
          error.response?.data?.detail || 'Failed to get user profile',
          error.response?.status
        )
      }
      throw new AuthCheckError('An unexpected error occurred')
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await authApi.post('/auth/logout')
    } catch (error) {
      // Continue with local logout even if API call fails
      console.error('Logout API call failed:', error)
    } finally {
      setAuthToken(null)
      // Clean up any token persisted by older app versions.
      localStorage.removeItem('token')
      localStorage.removeItem('token_expiry')
    }
  },

  // Verify token
  async verifyToken(): Promise<boolean> {
    try {
      await authApi.get('/auth/verify')
      return true
    } catch (error) {
      return false
    }
  },

  // Request a password reset email. Always resolves — the backend returns a
  // generic message regardless of whether the address is registered, so the
  // UI never leaks which emails have accounts.
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response = await authApi.post('/password-reset/request', { email })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.detail || 'リクエストに失敗しました')
      }
      throw new Error('An unexpected error occurred')
    }
  },

  // Complete a password reset using the token from the emailed link.
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await authApi.post('/password-reset/confirm', {
        token,
        new_password: newPassword,
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.detail || 'パスワードの更新に失敗しました')
      }
      throw new Error('An unexpected error occurred')
    }
  },
}

export default authService